// FILE: src/components/RegisteredUsersScreen.js
//
// Registered Users — Mission Control tool.
// Admin-only roster of real registered learners (internal/test accounts are
// excluded server-side by admin_registered_users / migration 049). Gives a
// clear, sortable view of who has registered and whether they've engaged —
// the data behind "how many people I reached out to have actually registered".

import React, { useEffect, useMemo, useState, useCallback } from 'react';

import { listRegisteredUsers } from '../../services/registeredUsersService';
import {
  rowStatus, STATUS_LABEL, STATUS_RANK, computeRosterStats,
} from '../../services/rosterMetrics';

// ── Friendly exam labels ─────────────────────────────────────────────────────
const EXAM_LABELS = {
  pmp: 'PMP',
  cpmai: 'CPMAI',
  'pmi-acp': 'PMI-ACP',
  'pmi-pmocp': 'PMI-PMOCP',
};
const examLabel = (id) => (id ? (EXAM_LABELS[id] || id) : '—');
// Comma-separated cert ids -> friendly label list.
const practicedLabel = (ids) => {
  if (!ids) return '—';
  return ids.split(',').map((id) => EXAM_LABELS[id] || id).join(', ');
};

// ── Exam Type ── which exams the learner has actually taken questions in.
//
// Prefers persisted attempt history. Falls back to the exam ids named by the
// learner's own answer events, which is the ONLY record that exists when a
// session never persisted — PM Pixel writes exam_sessions / question_attempts
// only when a session ENDS, so a learner who answers and then leaves saves
// nothing (migration 105 header has the worked example).
const examType = (r) => {
  if (r.practiced_exam_ids) {
    return { ids: r.practiced_exam_ids, source: 'attempts' };
  }
  if (r.answered_exam_ids) {
    return { ids: r.answered_exam_ids, source: 'events' };
  }
  return { ids: null, source: null };
};
const EXAM_TYPE_HINT = {
  attempts: null,
  events: 'From this learner\u2019s answer events — they answered here, but the session '
        + 'was never saved (PM Pixel only persists a session when it ends).',
};

// ── Date helpers ──────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};
// ── Funnel status ───────────────────────────────────────────────────────────
// Canonical definitions live in services/rosterMetrics.js (migration 104):
// Registered → Confirmed → Started → Activated. Unconfirmed email signups never
// reached the product, so they are an acquisition/delivery problem, not dormancy.

const daysAgo = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

// ── Sorting ───────────────────────────────────────────────────────────────────
const COLUMNS = [
  { key: 'email', label: 'Email', align: 'left' },
  { key: 'display_name', label: 'Name', align: 'left' },
  { key: 'registered_at', label: 'Registered', align: 'left' },
  { key: 'last_sign_in_at', label: 'Last sign-in', align: 'left' },
  { key: 'target_exam_id', label: 'Exam Date Set', align: 'left' },
  { key: 'practiced_exam_ids', label: 'Exam Type', align: 'left' },
  { key: 'answered_attempts', label: 'Answered', align: 'right' },
  { key: 'status', label: 'Status', align: 'left' },
];

function sortRows(rows, key, dir) {
  const mult = dir === 'asc' ? 1 : -1;
  const val = (r) => {
    switch (key) {
      case 'answered_attempts': return Number(r.answered_attempts) || 0;
      case 'status': return STATUS_RANK[rowStatus(r)];
      // Sort Exam Type on what the column actually shows, so an event-only
      // row sorts with its cert rather than with the blanks.
      case 'practiced_exam_ids': return (examType(r).ids || '').toLowerCase();
      case 'registered_at':
      case 'last_sign_in_at': return r[key] ? new Date(r[key]).getTime() : 0;
      default: return (r[key] || '').toString().toLowerCase();
    }
  };
  return [...rows].sort((a, b) => {
    const av = val(a); const bv = val(b);
    if (av < bv) return -1 * mult;
    if (av > bv) return 1 * mult;
    return 0;
  });
}

function toCsv(rows) {
  const head = ['Email', 'Name', 'Registered', 'Last sign-in', 'Confirmed', 'Exam Date Set', 'Exam date', 'Exam Type', 'Exam Type source',
    'Questions answered', 'Case-study answers', 'Question slots (incl. unanswered)', 'Status', 'Answered as guest before registering'];
  const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
  const lines = rows.map((r) => [
    r.email, r.display_name || '', fmtDate(r.registered_at), fmtDate(r.last_sign_in_at),
    r.email_confirmed === false ? 'No' : 'Yes',
    examLabel(r.target_exam_id), fmtDate(r.target_exam_date),
    practicedLabel(examType(r).ids), examType(r).source === 'events' ? 'answer events (session not saved)' : examType(r).source || '',
    r.answered_attempts == null ? '' : r.answered_attempts, r.case_answers == null ? '' : r.case_answers, r.attempts,
    r.legacy ? '' : STATUS_LABEL[rowStatus(r)], r.guest_answer_evidence ? 'Yes (ambiguous)' : '',
  ].map(esc).join(','));
  return [head.map(esc).join(','), ...lines].join('\r\n');
}

export default function RegisteredUsersScreen({ onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('registered_at');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listRegisteredUsers()
      .then((data) => setRows(data))
      .catch((e) => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'answered_attempts' || key === 'registered_at' || key === 'last_sign_in_at' ? 'desc' : 'asc');
    }
  };

  const sorted = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);

  const stats = useMemo(() => computeRosterStats(rows), [rows]);
  const show = (v) => (v == null ? '—' : v);

  const exportCsv = () => {
    const blob = new Blob([toCsv(sorted)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `registered-users-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="registered-users-screen" style={S.wrapper}>
      <div style={S.header}>
        <button data-testid="registered-users-back" style={S.backButton} onClick={onBack}>← Back</button>
        <span style={S.headerTitle}>👥 Registered Users</span>
        <button style={S.headerAction} onClick={load} disabled={loading}>⟳ Refresh</button>
        <button style={S.headerAction} onClick={exportCsv} disabled={loading || !rows.length}>⬇ Export CSV</button>
      </div>

      <div style={S.body}>
        <p style={S.intro}>
          Real registered learners only — your admin logins and the osoandard@ test
          account are excluded, so these numbers reflect genuine sign-ups.
          Activated means the learner answered at least one question (including
          case-study answers, or an answer recorded before a session failed to save).
        </p>

        {stats.legacy && (
          <div data-testid="roster-legacy-warning" style={S.error}>
            The database hasn’t been updated with the activation-evidence columns yet
            (migration 104), so Started, Activated, Dormant and Questions answered are
            not shown rather than shown under the old definition.
          </div>
        )}

        <div style={S.statRow}>
          <Stat label="Registered" value={stats.total} hint="real accounts" />
          <Stat label="Confirmed" value={stats.confirmed} hint="reached the product" />
          <Stat label="Started" value={show(stats.started)} hint="confirmed, entered an assessment" />
          <Stat label="Activated" value={show(stats.activated)} hint="≥1 question answered" tone="good" />
          <Stat label="Dormant" value={show(stats.dormant)} hint="confirmed, no answer yet" tone="warn" />
          <Stat label="Unconfirmed" value={stats.unconfirmed} hint="email never confirmed" tone="warn" />
          <Stat label="Questions answered" value={show(stats.questionsAnswered)} hint="answers submitted, incl. repeats" />
        </div>

        {!stats.legacy && (stats.guestOnly > 0 || stats.activatedViaEventOnly > 0) && (
          <p data-testid="roster-evidence-note" style={S.intro}>
            {stats.activatedViaEventOnly > 0 && (
              <>{stats.activatedViaEventOnly} activated learner{stats.activatedViaEventOnly === 1 ? '' : 's'} answered
                in the app but the session never saved (counted from the answer event). </>
            )}
            {stats.guestOnly > 0 && (
              <>{stats.guestOnly} confirmed account{stats.guestOnly === 1 ? '' : 's'} answered only as a guest before
                registering — flagged “guest?” and not counted as activated.</>
            )}
          </p>
        )}

        {loading && <div style={S.note}>Loading roster…</div>}
        {error && <div style={S.error}>Couldn’t load roster: {error}</div>}

        {!loading && !error && (
          <div style={S.card}>
            {rows.length === 0 ? (
              <div style={S.note}>No registered learners yet.</div>
            ) : (
              <table style={S.table}>
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        style={{ ...S.th, textAlign: c.align, cursor: 'pointer' }}
                        onClick={() => onSort(c.key)}
                      >
                        {c.label}{sortKey === c.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => {
                    const status = rowStatus(r);
                    const et = examType(r);
                    const dSign = daysAgo(r.last_sign_in_at);
                    return (
                      <tr key={r.user_id}>
                        <td style={{ ...S.td, ...S.emailCell }}>{r.email}</td>
                        <td style={S.td}>{r.display_name || '—'}</td>
                        <td style={S.td}>{fmtDate(r.registered_at)}</td>
                        <td style={S.td}>
                          {fmtDate(r.last_sign_in_at)}
                          {dSign != null && <span style={S.sub}>{dSign === 0 ? ' · today' : ` · ${dSign}d ago`}</span>}
                        </td>
                        <td style={S.td}>
                          {examLabel(r.target_exam_id)}
                          {r.target_exam_date && <span style={S.sub}>{` · ${fmtDate(r.target_exam_date)}`}</span>}
                        </td>
                        <td style={S.td}>
                          {et.ids
                            ? <span
                                style={et.source === 'events' ? S.studyingInferred : undefined}
                                title={EXAM_TYPE_HINT[et.source] || undefined}
                              >
                                {practicedLabel(et.ids)}
                                {et.source === 'events' && <span style={S.sub}> · not saved</span>}
                              </span>
                            : '—'}
                        </td>
                        <td
                          style={{ ...S.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                          title={r.legacy ? undefined : `${r.attempts} question slot(s) saved · ${r.case_answers} case-study answer(s)`}
                        >
                          {r.answered_attempts == null ? r.attempts : r.answered_attempts}
                        </td>
                        <td style={S.td}>
                          {!r.legacy && (
                            <span style={{
                              ...S.badge,
                              ...(status === 'activated' ? S.badgeActive
                                : status === 'started' ? S.badgeStarted
                                : status === 'not_started' ? S.badgeDormant
                                : S.badgeUnconfirmed),
                            }}>
                              {STATUS_LABEL[status]}
                            </span>
                          )}
                          {r.guest_answer_evidence && !r.activated && (
                            <span
                              style={{ ...S.badge, ...S.badgeGuest }}
                              title="Answered questions as a guest before registering on this device. Not counted as activated until guest activity is linked to accounts."
                            >guest?</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, hint, tone }) {
  const valColor = tone === 'good' ? '#2e7d32' : tone === 'warn' ? '#e65100' : '#1a1a2e';
  return (
    <div style={S.stat} data-testid={`roster-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div style={S.statLabel}>{label}</div>
      <div style={{ ...S.statValue, color: valColor }}>{value}</div>
      <div style={S.statHint}>{hint}</div>
    </div>
  );
}

// ── Styles (inline, matching the admin convention) ───────────────────────────
const S = {
  wrapper: { fontFamily: "'Segoe UI', system-ui, sans-serif", minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column' },
  header: { background: '#1a1a2e', color: '#fff', padding: '16px 28px', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 },
  backButton: { padding: '6px 14px', background: 'transparent', border: '1px solid #555', borderRadius: '4px', color: '#ccc', cursor: 'pointer', fontSize: '0.85rem' },
  headerTitle: { fontSize: '1.15rem', fontWeight: 'bold', flex: 1, letterSpacing: '0.5px' },
  headerAction: { padding: '6px 12px', background: 'rgba(255,255,255,0.08)', border: '1px solid #555', borderRadius: '4px', color: '#eee', cursor: 'pointer', fontSize: '0.82rem' },
  body: { flex: 1, padding: '32px', maxWidth: '1040px', margin: '0 auto', width: '100%', boxSizing: 'border-box' },
  intro: { fontSize: '0.85rem', color: '#666', marginBottom: '20px', lineHeight: 1.5, marginTop: 0 },
  statRow: { display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' },
  stat: { flex: '1 1 180px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px 18px' },
  statLabel: { fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' },
  statValue: { fontSize: '1.8rem', fontWeight: 700, lineHeight: 1.2, margin: '4px 0' },
  statHint: { fontSize: '0.75rem', color: '#999' },
  card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px 16px 16px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: { padding: '12px', borderBottom: '2px solid #e0e0e0', color: '#555', fontWeight: 600, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.5px', userSelect: 'none' },
  td: { padding: '12px', borderBottom: '1px solid #f0f0f0', color: '#333', verticalAlign: 'middle' },
  emailCell: { fontFamily: 'monospace', fontSize: '0.85rem' },
  sub: { color: '#aaa', fontSize: '0.78rem' },
  studyingInferred: { color: '#1565c0' },
  badge: { display: 'inline-block', padding: '2px 10px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' },
  badgeActive: { background: '#e8f5e9', color: '#2e7d32' },
  badgeUnconfirmed: { background: '#eceff1', color: '#546e7a', border: '1px solid #b0bec5' },
  badgeDormant: { background: '#fff3e0', color: '#e65100' },
  badgeStarted: { background: '#fffde7', color: '#8d6e00', border: '1px solid #ffe082' },
  badgeGuest: { background: '#e3f2fd', color: '#1565c0', marginLeft: '6px' },
  note: { padding: '24px', color: '#777', fontSize: '0.9rem' },
  error: { padding: '16px', background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: '8px', color: '#c62828', fontSize: '0.88rem' },
};
